import { useState, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { useNode } from '@/components/Recipe/FlowContext';
import type { NodeId } from 'web';

interface UseAvoidNodeOverflowOnPasteParams {
  nodeId: NodeId;
  padding?: number;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

export function useAvoidNodeOverflowOnPaste({ nodeId, textareaRef }: UseAvoidNodeOverflowOnPasteParams) {
  const { getViewport } = useReactFlow();
  const node = useNode(nodeId);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const onPaste = useCallback(() => {
    const { zoom, y: viewportY } = getViewport();
    const nodeYInViewport = node.position.y * zoom + viewportY;
    const availableSpaceBelow = window.innerHeight - nodeYInViewport;
    const calculatedMaxHeight = availableSpaceBelow / zoom;

    setMaxHeight(calculatedMaxHeight);

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.setSelectionRange(0, 0);
        textarea.scrollTop = 0;
      }
      setMaxHeight(undefined);
    });
  }, [textareaRef, getViewport, node.position.y]);

  return {
    maxHeight,
    onPaste,
  };
}
